import type { FunctionComponent } from 'react'

import {
	BodyL,
	FileUpload,
	FileUploadHeadingWithTooltip,
} from '@monthio/design-system'
import { FormHelperText } from '@monthio/react-components'
import { getIn, useFormikContext } from 'formik'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type { DefaultConsumerInputs } from '../../../types/questionnaire.types'

const emptyArray: File[] = []

const allowedFileTypes = [
	{ mime: 'image/jpeg', type: 'jpeg' },
	{ mime: 'image/jpg', type: 'jpg' },
	{ mime: 'image/jfif', type: 'jfif' },
	{ mime: 'image/png', type: 'png' },
	{ mime: 'image/bmp', type: 'bmp' },
	{ mime: 'image/webp', type: 'webp' },
	{ mime: 'application/pdf', type: 'pdf' },
]

const FileUploadWithMargin = styled(FileUpload)`
	margin-bottom: var(--spacing-xs);
`

const FileUploadWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const hasError = (errors: any, field: string) => {
	return getIn(errors, field, false) != false
}

const getErrMsg = (errors: any, field: string) => {
	if (hasError(errors, field)) {
		return getIn(errors, field)
	}
}

export const IdDocumentUpload: FunctionComponent = () => {
	const { values, setFieldValue, errors, touched, setFieldTouched } =
		useFormikContext<DefaultConsumerInputs>()
	const { t } = useTranslation()

	const fileUploadTranslations = useMemo(
		() => ({
			addFileButtonAriaLabel: t('add-file'),
			isRequiredText: t('required'),
			removeFileButtonAriaLabel: (file: File) =>
				t('remove-named-file', { fileName: file.name }),
			sizeRestrictionText: t('incomeVerification-max-file-size'),
		}),
		[t],
	)

	const handleFilesChange = useCallback(
		(files: readonly File[]) => {
			setFieldValue('uploadedFiles', files)
		},
		[setFieldValue],
	)

	const handleTouched = useCallback(() => {
		setFieldTouched('uploadedFiles', true)
	}, [setFieldTouched])

	return (
		<FileUploadWrapper>
			<BodyL id="id-document-upload-description">
				{t('personalInformationStep-consumerInputs-uploadIdDocument-describe')}
				{touched.uploadedFiles && errors.uploadedFiles ? (
					<FormHelperText error>
						{getErrMsg(errors, 'uploadedFiles')}
					</FormHelperText>
				) : null}
			</BodyL>
			<FileUploadWithMargin
				title={
					<FileUploadHeadingWithTooltip
						closeButtonText={t('close')}
						title={t(
							`allowed-applicantProvidedDocumentType-euIdOrRecidencePermit-documentType`,
						)}
						tooltip={t(
							`allowed-applicantProvidedDocumentType-euIdOrRecidencePermit-tooltip`,
						)}
					/>
				}
				onBlur={handleTouched}
				translations={fileUploadTranslations}
				acceptedFileTypes={allowedFileTypes}
				value={values.uploadedFiles ?? emptyArray}
				onChange={handleFilesChange}
				aria-describedby="id-document-upload-description"
			/>
		</FileUploadWrapper>
	)
}
